import { Injectable, inject } from '@angular/core'
import { Observable, of, delay, forkJoin } from 'rxjs'
import { ILead, TLeadStatus, ICrmData, ILeadData } from '../models/lead-info.model'
import { HttpClient } from '@angular/common/http'
import { map, catchError, shareReplay } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { API_ENDPOINTS, CRM_DEFAULTS } from '@app/core/constants/api.constants'
import { IProductsApiResponse } from '../models/product-api.model'
import {
    IAggregatedLeadResponse,
    ILeadPatchRequest,
    IValidateStageRequest,
    IValidateStageResponse,
    IValidateStageRequestBody,
    IStageData,
    IValidateStageFormData,
    IValidateStageWithOrderRequest,
    IValidateStageWithOrderRequestBody,
} from '../models/aggregated-lead.model'
import { ELeadStageTitle } from '@app/core/enum/client.enum'
import { API_PATH_SUFFIX } from '@app/core/constants/api.constants'
import { ILeadOrderLinkRequest, ILeadOrderLinkResponse } from '../models/lead.model'
import { mapStageToStageId } from '../helpers/lead-list.helper'
import { normalizeStageDataFeeItemsForApi } from '../utils/lead-information-fee-items.util'
import { normalizeStageDataCurriculumForApi } from '../utils/lead-information-stage-curriculum.util'

@Injectable({
    providedIn: 'root',
})
export class LeadService {
    private http = inject(HttpClient)

    private crmData: ICrmData | null = null

    private stagePatchUrl(): string {
        return `${environment.apiUrl}${API_ENDPOINTS.VALIDATE_STAGE}`
    }

    private loadCrmData(): Observable<ICrmData> {
        if (this.crmData) {
            return of(this.crmData)
        }
        return this.http.get<ICrmData>('assets/mock/crm-data.json').pipe(
            map((data: ICrmData) => {
                this.crmData = data
                return data
            }),
            catchError((error) => {
                this.crmData = { leads: {}, communicationRecords: [] }
                return of(this.crmData)
            }),
        )
    }

    private parseLead(leadData: ILeadData): ILead {
        return {
            ...leadData,
            createdAt: new Date(leadData.createdAt),
            updatedAt: new Date(leadData.updatedAt),
        }
    }

    getCurrentLead(): Observable<ILead> {
        return this.loadCrmData().pipe(
            map((data: ICrmData) => {
                const leadData = data.leads['lead-1']
                if (!leadData) {
                    throw new Error('Lead not found')
                }
                return this.parseLead(leadData)
            }),
            delay(500),
        )
    }

    updateLead(leadId: string, updates: Partial<ILead>): Observable<ILead> {
        return this.loadCrmData().pipe(
            map((data: ICrmData) => {
                const leadData = data.leads[leadId]
                if (!leadData) {
                    throw new Error('Lead not found')
                }

                const updatedLead: ILeadData = {
                    ...leadData,
                    ...updates,
                    createdAt:
                        updates.createdAt instanceof Date
                            ? updates.createdAt.toISOString()
                            : leadData.createdAt,
                    updatedAt: new Date().toISOString(),
                }

                data.leads[leadId] = updatedLead
                return this.parseLead(updatedLead)
            }),
            delay(800),
        )
    }

    updateLeadStatus(leadId: string, status: TLeadStatus): Observable<ILead> {
        return this.loadCrmData().pipe(
            map((data: ICrmData) => {
                const leadData = data.leads[leadId]
                if (!leadData) {
                    throw new Error('Lead not found')
                }

                const updatedLead: ILeadData = {
                    ...leadData,
                    status,
                    updatedAt: new Date().toISOString(),
                }

                data.leads[leadId] = updatedLead
                return this.parseLead(updatedLead)
            }),
            delay(500),
        )
    }

    // --- New API integrations ---

    private activeProducts$: Observable<Map<number, string>> | null = null

    /** Cached so multiple subscribers (e.g. funnel load + RM load) share one request and avoid "too many requests". */
    getActiveProducts(): Observable<Map<number, string>> {
        if (!this.activeProducts$) {
            const url = `${environment.apiUrl}${API_ENDPOINTS.PRODUCTS}?status=true`
            this.activeProducts$ = this.http.get<IProductsApiResponse>(url).pipe(
                map((res) => {
                    const mapping = new Map<number, string>()
                    res.data.forEach((product) => {
                        mapping.set(product.product_id, product.name)
                    })
                    return mapping
                }),
                shareReplay(1),
            )
        }
        return this.activeProducts$
    }

    getAggregatedLead(
        leadId: number = CRM_DEFAULTS.DEFAULT_LEAD_ID,
    ): Observable<IAggregatedLeadResponse> {
        const url = `${environment.apiUrl}${API_ENDPOINTS.AGGREGATED_LEAD}/${leadId}`
        return this.http.get<IAggregatedLeadResponse>(url)
    }

    patchLead(leadId: number, payload: ILeadPatchRequest): Observable<unknown> {
        const url = `${environment.apiUrl}${API_ENDPOINTS.LEAD_MANAGEMENT}/${leadId}`
        return this.http.patch(url, payload)
    }

    /** Link a created order to a lead */
    linkOrderToLead(leadId: number, orderId: number): Observable<ILeadOrderLinkResponse> {
        const url = `${environment.apiUrl}${API_ENDPOINTS.LEAD_MANAGEMENT}/${leadId}${API_PATH_SUFFIX.LEAD_ORDER}`
        const body: ILeadOrderLinkRequest = { order_id: orderId }
        return this.http.patch<ILeadOrderLinkResponse>(url, body)
    }

    saveLead(lead: ILead): Observable<ILead> {
        return this.loadCrmData().pipe(
            map((data: ICrmData) => {
                const updatedLead: ILeadData = {
                    ...lead,
                    createdAt: lead.createdAt.toISOString(),
                    updatedAt: new Date().toISOString(),
                }

                data.leads[lead.id] = updatedLead
                return this.parseLead(updatedLead)
            }),
            delay(1000),
        )
    }

    validateStage(payload: IValidateStageRequest): Observable<IValidateStageResponse> {
        const currentStageEnum = payload.current_stage ?? payload.currentStage ?? ELeadStageTitle.S0
        const targetStageEnum = payload.target_stage ?? payload.targetStage ?? currentStageEnum

        const current_stage = mapStageToStageId(currentStageEnum) ?? 0
        const target_stage = mapStageToStageId(targetStageEnum) ?? current_stage
        const lead_id = payload.lead_id ?? payload.leadId ?? 0

        let stage_data: IStageData = {}

        if (payload.stage_data) {
            stage_data = payload.stage_data
        } else if (payload.formData) {
            const formData = payload.formData

            // Mapping from formData keys to stage_data keys (only fields that exist in both interfaces)
            const formDataToStageDataMap: Partial<
                Record<keyof IValidateStageFormData, keyof IStageData>
            > = {
                academic_year: 'academic_year',
                projected_estimate: 'projected_estimate',
                conversion_probability: 'conversion_probability',
                principalName: 'principal_name',
                contact_id: 'contact_id',
                email: 'email',
                country_code: 'country_code',
                mobile_number: 'mobile_number',
                address_line: 'address_line',
                postal_code: 'postal_code',
                city: 'city',
                state: 'state',
                curriculum_id: 'curriculum_id',
                organisation_curriculum_id: 'organisation_curriculum_id',
                curriculum_detail: 'curriculum_detail',
                curriculum: 'curriculum',
                curriculum_delete_ids: 'curriculum_delete_ids',
                academic_fee_delete_ids: 'academic_fee_delete_ids',
                fee_items: 'fee_items',
            }

            // Only include keys present in formData (supports PATCH semantics).
            Object.entries(formDataToStageDataMap).forEach(([formKey, stageKey]) => {
                if (
                    stageKey &&
                    formKey in formData &&
                    formData[formKey as keyof IValidateStageFormData] !== undefined
                ) {
                    ;(stage_data as Record<string, unknown>)[stageKey] =
                        formData[formKey as keyof IValidateStageFormData]
                }
            })
        }

        stage_data = normalizeStageDataFeeItemsForApi(stage_data)
        stage_data = normalizeStageDataCurriculumForApi(stage_data)

        if (lead_id) {
            stage_data = { ...stage_data, lead_id: Number(lead_id) || 0 }
        }

        const body: IValidateStageRequestBody = {
            organisation_code: payload.organisation_code,
            current_stage,
            target_stage,
            vertical_id: payload.vertical_id,
            lead_id,
            stage_data,
        }

        // Use PATCH to align with backend semantics for stage transitions
        return this.http.patch<IValidateStageResponse>(this.stagePatchUrl(), body)
    }

    /**
     * Update stage using order_id (for S4 to S5 transition after proposal approval and creation)
     */
    validateStageWithOrder(
        payload: IValidateStageWithOrderRequest,
    ): Observable<IValidateStageResponse> {
        const body: IValidateStageWithOrderRequestBody = {
            order_id: payload.order_id,
            organisation_code: payload.organisation_code,
            current_stage: payload.current_stage,
            target_stage: payload.target_stage,
            vertical_id: payload.vertical_id,
        }

        return this.http.patch<IValidateStageResponse>(this.stagePatchUrl(), body)
    }
}
